import xor from "lodash/xor";
import { filterIndexDataByDate, getDateFromTimeframe } from "utils/date";

import { theme } from "styles";

import { keyEvents } from "./data/keyEvents";

export const INDEX_CHART_DOMAIN = [60, 160];
export const INDEX_CHART_TICKS = [80, 100, 120, 140, 160];
export const USED_VOLUME_CHART_DOMAIN = [15000, 150000];
export const EV_VOLUME_CHART_DOMAIN = [50, 3000];
export const COMBINED_VOLUME_CHART_DOMAIN = [1500, 160000];

export const FREQUENCY_TYPES = {
  WEEKLY: "WEEKLY",
  MONTHLY: "MONTHLY",
};

export const CHART_LABELS = {
  VOLUME: "Auc. Vol.",
  INDEX: "Index Value",
  KEY_EVENTS: "Key Events",
};

export const TIME_FRAMES = {
  "1Y": "1Y",
  "3Y": "3Y",
  "5Y": "5Y",
  "10Y": "10Y",
  ALL: "ALL",
};

export const axisStyles = {
  fontFamily: "Readex Pro Light",
  fontSize: "13px",
  fontStyle: "normal",
  fontWeight: "300",
  lineHeight: "165%",
  overflow: "visible",
  fill: theme.colors.lightSecondary,
};

export const CHART_SERIES: ChartSeries[] = [
  {
    id: "emIndex",
    label: "Exponential Used Vehicle Index",
    shortLabel: "EM - Used Vehicle Index",
    isThirdParty: false,
    hasVolume: true,
    color: theme.colors.chart.blue,
    styles: {
      bar: { color: theme.colors.chart.blue },
    },
  },
  {
    id: "emEvIndex",
    label: "Exponential Used Electric Vehicle Index",
    shortLabel: "EM - EV Index",
    isThirdParty: false,
    hasVolume: true,
    color: theme.colors.chart.green,
    styles: {
      bar: { color: theme.colors.chart.lightGreen },
      line: { color: theme.colors.chart.green },
    },
  },
  {
    id: "dow",
    label: "Dow Jones US Automobiles",
    shortLabel: "DJ US Automobiles",
    isThirdParty: true,
    hasVolume: false,
    color: theme.colors.chart.pink,
    styles: {
      line: {
        color: theme.colors.chart.pink,
      },
    },
  },
  {
    id: "sp500",
    label: "S&P 500",
    shortLabel: "S&P 500",
    isThirdParty: true,
    hasVolume: false,
    color: theme.colors.chart.orange,
    styles: {
      line: {
        color: theme.colors.chart.orange,
      },
    },
  },
  {
    id: "cpi",
    label: "CPI",
    shortLabel: "CPI",
    isThirdParty: true,
    hasVolume: false,
    color: theme.colors.chart.murple,
    styles: {
      line: {
        color: theme.colors.chart.murple,
      },
    },
  },
];

export const getChartColorById = (
  series: ChartSeries[],
  seriesId: string,
  type?: string,
) => {
  const chart = series.find(({ id }) => id === seriesId);
  const styles = chart?.styles;

  if (type) {
    return styles?.[type]?.color ?? undefined;
  }

  return chart?.color;
};

export type IndexData = {
  date: Date;
  index_entries: IndexEntry[];
};

export type TableDataEntry = {
  label: string;
  data: {
    change: number;
    high: number;
    low: number;
    price: number;
    volume: number;
  };
};

export type TableData = Record<string, TableDataEntry>;

export type IndexEntry = {
  index_type: string;
  index_value: number | null;
  volume: number | null;
};

type ChartColorByType = Record<
  string,
  {
    color: string;
  }
>;

export type ChartSeries = {
  id: string;
  label: string;
  shortLabel: string;
  styles: ChartColorByType;
  color: string;
  isThirdParty: boolean;
  hasVolume: boolean;
};

export type ChartSeriesEntry = {
  [key: string]: number | string;
};

type ChartData = Record<string, number | string>;

export function MMDDYYYYStringToDate(dateString: string): Date {
  // Split the date string into its components
  const [month, day, year] = dateString.split("/").map(Number);

  // Create a new date object
  const date = new Date(year, month - 1, day); // Months are zero-based
  return date;
}

export const getChartData = (data: IndexData[], timeFrame: string) => {
  const startDate = getDateFromTimeframe(timeFrame);
  const filteredData = filterIndexDataByDate(data, startDate);

  const chartData = [];
  for (const [index, { date, index_entries }] of filteredData.entries()) {
    chartData.push({
      date,
      ...CHART_SERIES.reduce((acc, { hasVolume, id, label }) => {
        const indexEntry = index_entries.find(
          ({ index_type }) => index_type === label,
        );
        let event;
        if (index > 0 && index < filteredData.length - 1) {
          const prev_time = filteredData[index - 1].date.getTime();
          const next_time = filteredData[index + 1].date.getTime();
          const time = date.getTime();
          event = keyEvents.find(
            ({ date: eventDate }) =>
              Math.abs(time - eventDate.getTime()) <=
                Math.abs(prev_time - eventDate.getTime()) &&
              Math.abs(time - eventDate.getTime()) <
                Math.abs(next_time - eventDate.getTime()),
          );
        } else {
          event = keyEvents.find(({ date: eventDate }) => date === eventDate);
        }

        if (!indexEntry) {
          return { ...acc };
        }
        return {
          ...acc,
          [id]: indexEntry?.index_value,
          ...(hasVolume && {
            [`${id}Volume`]: indexEntry?.volume,
          }),
          ...(event && {
            event: 50,
            eventDescription: event.description,
          }),
        };
      }, {}),
    });
  }

  return chartData;
};

const getFirstIndexPrice = (
  chartData: ChartData[],
  indexName: string,
): number => {
  const min = [...chartData].find((item) => item[indexName])?.[indexName];
  const max = [...chartData.reverse()].find((item) => item[indexName])?.[
    indexName
  ];

  return (
    Number([...chartData].find((item) => item[indexName])?.[indexName]) || 0
  );
};

const getLastIndexPrice = (chartData: ChartData[], indexName: string): number =>
  Number(
    [...chartData.reverse()].find((item) => item[indexName])?.[indexName],
  ) || 0;

const getAccumulatedVolume = (chartData: ChartData[], indexVolume: string) =>
  chartData.reduce((acc, item) => {
    if (!item[indexVolume]) {
      return acc;
    }

    return acc + Number(item[indexVolume]);
  }, 0);

const getIndexHigh = (chartData: ChartData[], indexName: string) => {
  const test = chartData.reduce((acc, item) => {
    if (!item[indexName]) {
      return acc;
    }

    return Math.max(acc || Number(item[indexName]), Number(item[indexName]));
  }, 0);

  return test;
};

const getIndexLow = (chartData: ChartData[], indexName: string) =>
  chartData.reduce((acc, item) => {
    if (!item[indexName]) {
      return acc;
    }

    return Math.min(acc || Number(item[indexName]), Number(item[indexName]));
  }, 0);

export const filterThirdPartySeriesWithEntries = (
  data: ChartSeriesEntry[],
  thirdPartyIndexName: string,
) =>
  data.filter(
    (indexes) =>
      Object.keys(indexes).includes(thirdPartyIndexName) ||
      Object.keys(indexes).includes("emIndex") ||
      Object.keys(indexes).includes("emEvIndex"),
  );

export const getTableData = (filteredChartData: ChartData[]): TableData =>
  CHART_SERIES.reduce((acc, { id, shortLabel }) => {
    const high = getIndexHigh(filteredChartData, id);
    const low = getIndexLow(filteredChartData, id);
    const lastIndexPrice = getLastIndexPrice(filteredChartData, id);
    const firstIndexPrice = getFirstIndexPrice(filteredChartData, id);
    const change = lastIndexPrice / firstIndexPrice;

    return {
      ...acc,
      [id]: {
        label: shortLabel,
        data: {
          high,
          low,
          change,
          price: lastIndexPrice,
          volume:
            id === "emIndex" || id === "emEvIndex"
              ? getAccumulatedVolume(filteredChartData, `${id}Volume`)
              : 0,
        },
      },
    };
  }, []) || {};

export const toggleSeriesItem = (
  item: string,
  selectedIndexes: string[],
  onToggleSelectedIndexes: any,
) => {
  const isThirdParty = CHART_SERIES.find(({ id }) => id === item)?.isThirdParty;

  if (isThirdParty) {
    if (selectedIndexes.includes(item)) {
      return onToggleSelectedIndexes((state: string[]) =>
        state.filter((series) => series !== item),
      );
    }
    const emSeries = CHART_SERIES.filter(
      ({ isThirdParty }) => !isThirdParty,
    ).map(({ id }) => id);

    const selectedEmSeries = selectedIndexes.filter((series) =>
      emSeries.includes(series),
    );
    return onToggleSelectedIndexes([...selectedEmSeries, item]);
  }

  return onToggleSelectedIndexes((state: string[]) => xor(state, [item]));
};

export const getFilteredEventData = (filteredData: ChartData[]) =>
  filteredData.map(({ date, event, eventDescription }) => ({
    date,
    event,
    eventDescription,
  }));

const firstPartyIndexNames = CHART_SERIES.filter(
  ({ isThirdParty }) => isThirdParty === false,
).map(({ id }) => id);

export const getSelectedThirdPartyIndexName = (selectedIndexes: string[]) =>
  selectedIndexes.filter((index) => !firstPartyIndexNames.includes(index))?.[0];

export const getIsShowingThirdPartyData = (selectedIndexes: string[]) =>
  selectedIndexes.some(
    (series) => CHART_SERIES.find(({ id }) => id === series)?.isThirdParty,
  );

export const getFilteredData = (
  data: ChartData[],
  selectedIndexes: string[],
): ChartSeriesEntry[] => {
  const isThirdPartyDataSelected = selectedIndexes.some(
    (series) => CHART_SERIES.find(({ id }) => id === series)?.isThirdParty,
  );

  return !isThirdPartyDataSelected
    ? data.filter(({ emIndex }) => Boolean(emIndex))
    : filterThirdPartySeriesWithEntries(
        data,
        getSelectedThirdPartyIndexName(selectedIndexes),
      );
};

export const getFrequencyTypeByTimeFrame = (timeFrame: string) =>
  timeFrame === "10Y" || timeFrame === "ALL"
    ? FREQUENCY_TYPES.MONTHLY
    : FREQUENCY_TYPES.WEEKLY;
